import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CONSENT_KEY = 'cookie_consent';

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(thirdParty) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ thirdParty, timestamp: Date.now() })
    );
  } catch {
    // localStorage unavailable
  }
}

/**
 * Clears stored consent and re-shows the cookie banner.
 * Call this when the user wants to review their cookie preferences.
 */
export function requestCookieConsentReview() {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('tmg-cookie-consent-reset'));
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const firstBtnRef = useRef(null);

  useEffect(() => {
    if (getCookieConsent() === null) setVisible(true);
    function handleReset() { setVisible(true); }
    window.addEventListener('tmg-cookie-consent-reset', handleReset);
    return () => window.removeEventListener('tmg-cookie-consent-reset', handleReset);
  }, []);

  useEffect(() => {
    if (visible) firstBtnRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  function accept(thirdParty) {
    saveConsent(thirdParty);
    setVisible(false);
    window.dispatchEvent(new CustomEvent('tmg-cookie-consent-saved', { detail: { thirdParty } }));
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-heading"
      className="fixed bottom-0 left-0 right-0 z-50 t-bg-surface t-border border-t shadow-2xl"
    >
      <h2 id="cookie-banner-heading" className="sr-only">{t('cookies.bannerTitle')}</h2>
      <div className="max-w-4xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="text-sm t-text-secondary flex-1">
          {t('cookies.descriptionPre')}{' '}
          {t('cookies.descriptionPost')}{' '}
          <Link
            to="/privacy"
            className="t-text-accent underline hover:opacity-80 t-focus-ring rounded"
          >
            {t('cookies.privacyPolicy')}
          </Link>
        </p>
        <div className="shrink-0">
          <button
            ref={firstBtnRef}
            type="button"
            onClick={() => accept(false)}
            className="text-sm px-4 py-2 rounded-lg t-btn-primary font-medium transition-colors t-focus-ring"
          >
            {t('cookies.essentialOnly')}
          </button>
        </div>
      </div>
    </div>
  );
}
